import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { change, reduxForm } from 'redux-form';

import { Typography } from '@material-ui/core';

import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

import ConfirmContext from 'contexts/ConfirmContext';

import BookingInformation from '../booking-information/BookingInformation';

import { currencyFormatter } from 'utils/prices/formatter';
import { getTotalPrice } from 'utils/book-rental-items/rentalTotalPrice';

import { LOCALES_OBJ } from 'constants/base';

import { getFormByCurrentLanguage } from 'utils/form-localization/formLocalization';

import useStyles from './styles';

const FIXED_PRICES = 'pricesFixedForm';
const DAILY_PRICES = 'pricesDailyForm';
const WEEKLY_PRICES = 'pricesWeeklyForm';
const HOURLY_PRICES = 'pricesHourlyForm';

const Units = ({ units, pricesType, dates, currency, productsMenuIndex }) => {
  const classes = useStyles();
  const [t] = useTranslation();

  const startDate = dates && dates.startDate;
  const endDate = dates && dates.endDate;
  const diffTime = Math.abs(endDate - startDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const pluralDays = diffDays > 1 ? t('Days') : t('Day');


  const getTotalPrice = (unit, amount = 1) => {
    switch (pricesType) {
      case DAILY_PRICES:
        if (unit.breakdown && unit.breakdown.length > 0) {
         
          const breakdownTotal = unit.breakdown.reduce((total, day) => {
            return total + (day.value || 0);
          }, 0);
          return breakdownTotal * amount;
        }
        return (unit.currencyValue || unit.value || 0) * amount;

      case FIXED_PRICES:
        return (unit.currencyValue || unit.value || 0) * amount;

      case WEEKLY_PRICES:
        return (unit.currencyValue || unit.value || 0) * amount;

      case HOURLY_PRICES:
        const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
        return (
          (unit.currencyValue || unit.value || 0) * amount * (diffHours || 1)
        );

      default:
        return (unit.currencyValue || unit.value || 0) * amount;
    }
  };

  const hasCombinedBreakdown = unit => {
    return unit.isCombined && unit.breakdown && unit.breakdown.length > 0;
  };

  const getBreakdownWithDates = unit => {
    if (!unit.breakdown || !startDate) return unit.breakdown;

    return unit.breakdown.map((day, index) => {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + index);
      const formattedDate = moment(currentDate).format('MMM DD');

      return {
        ...day,
        formattedDate,
        perDayPrice: day.value, 
        total: day.value, 
        days: 1 
      };
    });
  };

  const renderPriceDisplay = (unit, amount = 1) => {
    const totalPrice = currencyFormatter(getTotalPrice(unit, amount));
    const displayCurrency = unit.currency || currency;

    switch (pricesType) {
      case DAILY_PRICES:
        const totalDays = unit.days || diffDays;

        if (unit.breakdown && unit.breakdown.length > 0) {
          const dailyBreakdownWithDates = getBreakdownWithDates(unit);
          const breakdownTotal = unit.breakdown.reduce(
            (total, day) => total + (day.value || 0),
            0
          );

          return (
            <>
              <Typography variant="body1" className={classes.bookingColumn}>
                {totalDays} {pluralDays} × {amount} {t('units')}
                <div className={classes.priceBreakdown}>
                  <div className={classes.breakdownLabel}>
                    {t('Daily rate breakdown')}:
                  </div>
                  {dailyBreakdownWithDates.map((day, index) => (
                    <div key={index} className={classes.breakdownDay}>
                      {day.formattedDate} ({day.day}):{' '}
                      {currencyFormatter(day.perDayPrice || day.value)}
                      {displayCurrency} × {day.days || 1} {t('days')} ={' '}
                      {currencyFormatter(day.total || day.value)}
                      {displayCurrency}
                    </div>
                  ))}
                  <div className={classes.breakdownTotal}>
                    {t('Total per unit')}: {currencyFormatter(breakdownTotal)}{' '}
                    {displayCurrency}× {amount} {t('units')}
                  </div>
                </div>
              </Typography>
              <Typography variant="h3">
                <span className={classes.currency}>{displayCurrency}</span>{' '}
                {totalPrice}
              </Typography>
            </>
          );
        } else {
         
          const dailyPrice = unit.currencyValue || unit.value || 0;
          const totalForUnit = dailyPrice * totalDays;
          return (
            <>
              <Typography variant="body1" className={classes.bookingColumn}>
                {totalDays} {pluralDays} × {amount} {t('units')}
                <div className={classes.priceBreakdown}>
                  <div className={classes.breakdownItem}>
                    {t('Daily rate')}: {currencyFormatter(dailyPrice)}{' '}
                    {displayCurrency} {t('per day')}× {totalDays} {t('days')} ={' '}
                    {currencyFormatter(totalForUnit)} {displayCurrency}
                  </div>
                  <div className={classes.breakdownTotal}>
                    {t('Total per unit')}: {currencyFormatter(totalForUnit)}{' '}
                    {displayCurrency}× {amount} {t('units')}
                  </div>
                </div>
              </Typography>
              <Typography variant="h3">
                <span className={classes.currency}>{displayCurrency}</span>{' '}
                {totalPrice}
              </Typography>
            </>
          );
        }

      case WEEKLY_PRICES:
        if (hasCombinedBreakdown(unit)) {
          const combinedBreakdownTotal = unit.breakdown.reduce(
            (total, period) => total + (period.value || period.total || 0),
            0
          );

          return (
            <>
              <Typography variant="body1" className={classes.bookingColumn}>
                {unit.days || diffDays} {pluralDays} × {amount} {t('units')}
                <div className={classes.priceBreakdown}>
                  <div className={classes.breakdownLabel}>
                    {t('Weekly rate breakdown')}:
                  </div>
                  {unit.breakdown.map((period, index) => (
                    <div key={index} className={classes.breakdownDay}>
                      {period.type}:{' '}
                      {currencyFormatter(period.perDayPrice || period.value)}
                      {displayCurrency} × {period.days} {t('days')} ={' '}
                      {currencyFormatter(period.total || period.value)}
                      {displayCurrency}
                    </div>
                  ))}
                  <div className={classes.breakdownTotal}>
                    {t('Total per unit')}:{' '}
                    {currencyFormatter(combinedBreakdownTotal)}{' '}
                    {displayCurrency}× {amount} {t('units')}
                  </div>
                </div>
              </Typography>
              <Typography variant="h3">
                <span className={classes.currency}>{displayCurrency}</span>{' '}
                {totalPrice}
              </Typography>
            </>
          );
        } else {
          const periodType =
            unit.type === 'weekday' ? t('Weekdays') : t('Weekends');
          const perDayPrice =
            unit.perDayPrice || unit.currencyValue || unit.value;
          const daysCount = unit.days || diffDays;
          const periodTotal = perDayPrice * daysCount;

          return (
            <>
              <Typography variant="body1" className={classes.bookingColumn}>
                {daysCount} {periodType} × {amount} {t('units')}
                <div className={classes.priceBreakdown}>
                  <div className={classes.breakdownItem}>
                    {periodType}: {currencyFormatter(perDayPrice)}{' '}
                    {displayCurrency} {t('per day')}× {daysCount} {t('days')} ={' '}
                    {currencyFormatter(periodTotal)} {displayCurrency}
                  </div>
                  <div className={classes.breakdownTotal}>
                    {t('Total per unit')}: {currencyFormatter(periodTotal)}{' '}
                    {displayCurrency}× {amount} {t('units')}
                  </div>
                </div>
              </Typography>
              <Typography variant="h3">
                <span className={classes.currency}>{displayCurrency}</span>{' '}
                {totalPrice}
              </Typography>
            </>
          );
        }

      case FIXED_PRICES:
        const start = startDate ? moment(startDate) : null;
        const end = endDate ? moment(endDate) : start;

        const totalDaysFixed =
          unit.days || (start && end ? end.diff(start, 'days') + 1 : 1);

        const totalPriceForAllDays = unit.currencyValue || unit.value || 0;
        const perDayPrice = totalPriceForAllDays / totalDaysFixed;
        const fixedTotal = perDayPrice * totalDaysFixed * amount;

        return (
          <>
            <Typography variant="body1" className={classes.bookingColumn}>
              <div className={classes.priceBreakdown}>
                <div className={classes.breakdownTotal}>
                  {currencyFormatter(perDayPrice)} {displayCurrency} × {totalDaysFixed}{' '}
                  {t('days')} × {amount} {t('units')}
                </div>
              </div>
            </Typography>

            <Typography variant="h3" className={classes.bookingColumn}>
              <span className={classes.currency}>{displayCurrency}</span>{' '}
              {currencyFormatter(fixedTotal)}
            </Typography>
          </>
        );

      case HOURLY_PRICES:
        const hourPriceValue = unit.currencyValue || unit.value || 0;

       
        let hourlyTotal = 0;
        let totalHours = 0;

        if (unit.breakdown && unit.breakdown.length > 0) {
         
          hourlyTotal = unit.breakdown.reduce(
            (total, hourSlot) => total + (hourSlot.value || 0),
            0
          );
          totalHours = unit.breakdown.length;
        } else {
         
          const startMoment = moment(unit.startTime || startDate);
          const endMoment = moment(unit.endTime || endDate);
          totalHours =
            unit.hours || Math.ceil(endMoment.diff(startMoment, 'hours', true));
          hourlyTotal = hourPriceValue * totalHours;
        }

        const finalTotal = hourlyTotal * amount;

        
        const startMoment = moment(unit.startTime || startDate);
        const endMoment = moment(unit.endTime || endDate);
        const dateDisplay = startMoment.format('MMM DD, YYYY');
        const timeRangeDisplay = `${startMoment.format(
          'HH:mm'
        )} – ${endMoment.format('HH:mm')}`;

        return (
          <>
            <div className={classes.bookingColumn}>
              <Typography variant="body1" component="div">
                {dateDisplay} {timeRangeDisplay} × {amount} {t('units')}
              </Typography>
              <div className={classes.priceBreakdown}>
                {unit.breakdown && unit.breakdown.length > 0 ? (
                  <>
                    <Typography
                      variant="body2"
                      className={classes.breakdownLabel}
                    >
                      {t('Hourly rate breakdown')}:
                    </Typography>
                    {unit.breakdown.map((hourSlot, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        className={classes.breakdownHour}
                      >
                        {hourSlot.hour}: {currencyFormatter(hourSlot.value)}{' '}
                        {displayCurrency}
                      </Typography>
                    ))}
                    <Typography
                      variant="body2"
                      className={classes.breakdownTotal}
                    >
                      {t('Total per unit')}: {currencyFormatter(hourlyTotal)}{' '}
                      {displayCurrency}× {amount} {t('units')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      className={classes.breakdownItem}
                    >
                      {t('Hourly rate')}: {currencyFormatter(hourPriceValue)}{' '}
                      {displayCurrency} {t('per hour')}× {totalHours}{' '}
                      {totalHours > 1 ? t('hours') : t('hour')} ={' '}
                      {currencyFormatter(hourlyTotal)} {displayCurrency}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={classes.breakdownTotal}
                    >
                      {t('Total per unit')}: {currencyFormatter(hourlyTotal)}{' '}
                      {displayCurrency}× {amount} {t('units')}
                    </Typography>
                  </>
                )}
              </div>
            </div>
            <Typography variant="h3" className={classes.bookingColumn}>
              <span className={classes.currency}>{displayCurrency}</span>{' '}
              {currencyFormatter(finalTotal)}
            </Typography>
          </>
        );

      default:
        const defaultPriceValue = unit.currencyValue || unit.value || 0;
        const defaultPrice = currencyFormatter(defaultPriceValue);
        const defaultTotal = defaultPriceValue * amount;
        return (
          <>
            <Typography variant="body1" className={classes.bookingColumn}>
              <span className={classes.currency}>{displayCurrency}</span>{' '}
              {defaultPrice}
              {amount > 1 && ` × ${amount} ${t('units')}`}
            </Typography>
            <Typography variant="h3" className={classes.bookingColumn}>
              <span className={classes.currency}>{displayCurrency}</span>{' '}
              {currencyFormatter(defaultTotal)}
            </Typography>
          </>
        );
    }
  };

  return (
    <div>
      {units &&
        units.map((unit, index) => {
          if (!unit || !parseInt(unit.availableUnits)) return null;

          const amount = parseInt(unit.availableUnits);

          return (
            <div
              key={unit.id || index}
              className={classNames(classes.generalRow, classes.unitRow)}
            >
              {renderPriceDisplay(unit, amount)}
            </div>
          );
        })}
    </div>
  );
};

const AddOns = ({
  addOns,
  currency,
  changeFormFieldValue,
  products,
  isStatic
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();
  const confirm = React.useContext(ConfirmContext);

  const selectedLanguage = i18n.language;

  const isAddOns = !!addOns.filter(item => item.value && parseInt(item.value))
    .length;

  const deleteAddOn = deletedAddOn => {
    confirm({
      title: 'Are you sure you want to delete it?',
      confirmationText: 'Delete',
      cancellationText: 'Back'
    })
      .then(() => {
        changeFormFieldValue(
          'addOns',
          addOns.filter(item => item !== deletedAddOn)
        );
      })
      .catch(() => {});
  };

  if (!isAddOns) return null;

  return (
    <div className={classes.accommodationCheckout}>
      <Typography
        variant="h4"
        classes={{ root: classNames(classes.generalRow, classes.listTitle) }}
      >
        {t('Add-ons')}
      </Typography>

      {addOns.map((item, index) => {
        if (!item.value || !parseInt(item.value)) return;
        const form = getFormByCurrentLanguage(
          selectedLanguage,
          item,
          item['Norwegian']
        );
        const amount = item.value;
        const price = currencyFormatter(item[currency]);
        const totalPrice = currencyFormatter(item[currency] * amount);
        return (
          <div key={index} className={classes.checkoutInfo}>
            <div className={classes.generalRow}>
              <Typography variant="h3" className={classes.bookingTitle}>
                {form.title}
              </Typography>
              {!isStatic && (
                <DeleteOutlineIcon
                  className={classes.deleteIcon}
                  onClick={() => deleteAddOn(item)}
                />
              )}
            </div>
            <div className={classes.generalRow}>
              <Typography variant="body1">
                <span className={classes.currency}>{currency}</span> {price} ×{' '}
                {amount}
              </Typography>
              <Typography variant="h3">
                <span className={classes.currency}>{currency}</span>{' '}
                {totalPrice}
              </Typography>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const GeneralCheckout = ({
  changeFormFieldValue,
  widgetForm,
  productData,
  currency,
  isStatic,
  productsMenuIndex
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();

  const selectedLanguage = i18n.language;

  const values = widgetForm.values;
  const generalForm = productData.generalForm;
  const generalFormNorwegian = productData['generalForm-Norwegian'];
  const pricesType = productData.pricesType;
  const units = widgetForm.values.availableUnits;


  let dates = values.dates;
  const startingDate =
    values.startingDate &&
    moment(values.startingDate)
      .locale(LOCALES_OBJ[selectedLanguage])
      .format('MMM DD, YYYY');
  const endingDate =
    values.endingDate &&
    moment(values.endingDate)
      .locale(LOCALES_OBJ[selectedLanguage])
      .format('MMM DD, YYYY');
  const startTime = values.startTime ? values.startTime.startTime : null;
  const timeType = values.startTime ? values.startTime.timeType : null;
  const duration = values.startTime ? values.startTime.duration : null;
  const startDate =
    dates &&
    moment(dates.startDate)
      .locale(LOCALES_OBJ[selectedLanguage])
      .format('MMM DD, YYYY');
  const endDate =
    dates &&
    moment(dates.endDate)
      .locale(LOCALES_OBJ[selectedLanguage])
      .format('MMM DD, YYYY');
  const checkIn =
    generalForm.checkInAfter && moment(generalForm.checkInAfter).format('H:mm');
  const checkOut =
    generalForm.checkOutBefore &&
    moment(generalForm.checkOutBefore).format('H:mm');

  const addOns = values.addOns;

  if (isStatic && !dates && startingDate && endingDate) {
    isAccommodation = false;
    dates = {
      startDate: values.startingDate,
      endDate: values.endingDate
    };
  }

  if (isStatic && values.multiDates && values.multiDates.endDate) {
    isAccommodation = false;
    dates = {
      startDate: values.multiDates.startDate,
      endDate: values.multiDates.endDate
    };
  }

  return (
    <>
      <div className={classes.accommodationCheckout}>
        <Typography
          variant="h4"
          classes={{ root: classNames(classes.generalRow, classes.listTitle) }}
        >
          {t('Rental Items')}
        </Typography>
        <div className={classes.checkoutInfo}>
          <div className={classes.generalRow}>
            <Typography variant="h3" className={classes.bookingTitle}>
              {
                getFormByCurrentLanguage(
                  selectedLanguage,
                  generalForm,
                  generalFormNorwegian
                ).title
              }
            </Typography>
          </div>
          <div className={classes.generalRow}>
            {startingDate && !endingDate && (
              <Typography variant="body1">
                {startingDate}{' '}
                {startTime && `· ${startTime} · ${duration} ${t(timeType)}`}
              </Typography>
            )}
            {startingDate && endingDate && (
              <Typography variant="body1">
                {startingDate} {' - '} {endingDate}
              </Typography>
            )}
            {startDate && (
              <Typography variant="body1">
                {startDate} – {endDate}
              </Typography>
            )}
          </div>
          {checkIn && (
            <div className={classes.generalRow}>
              <Typography variant="body1">
                {t('Check-in')}: {checkIn} - {t('Check-out')}: {checkOut}
              </Typography>
            </div>
          )}
        </div>
        <Units
          units={units}
          pricesType={pricesType}
          dates={dates}
          currency={currency}
          productsMenuIndex={productsMenuIndex}
        />
      </div>

      <AddOns
        addOns={addOns}
        currency={currency}
        changeFormFieldValue={changeFormFieldValue}
        isStatic={isStatic}
      />
    </>
  );
};

export const Total = ({ widgetForm, productData, currency, isStatic }) => {
  const classes = useStyles();
  const [t] = useTranslation();

  let dates = widgetForm.values.dates;
  const addOns = widgetForm.values.addOns;

  const startingDate = widgetForm.values.startingDate;
  const endingDate = widgetForm.values.endingDate;

  if (isStatic && !dates && startingDate && endingDate) {
    dates = {
      startDate: startingDate,
      endDate: endingDate
    };
  }

  if (
    isStatic &&
    !dates &&
    widgetForm.values.multiDates &&
    widgetForm.values.multiDates.endDate
  ) {
    dates = {
      startDate: widgetForm.values.multiDates.startDate,
      endDate: widgetForm.values.multiDates.endDate
    };
  }

  const {
    multiDates,
    availableUnits,
    startBookingsDate,
    startTime,
    endTime
  } = widgetForm.values;

  const totalPrice = currencyFormatter(
    getTotalPrice(
      productData,
      startBookingsDate,
      endTime,
      startTime,
      multiDates || dates,
      availableUnits,
      addOns,
      currency
    )
  );

  return (
    <div className={classes.totalRow}>
      <Typography variant="h3" className={classes.bookingTitle}>
        {t('Total')}
      </Typography>
      <Typography variant="h3">
        <span className={classes.currency}>{currency}</span> {totalPrice}
      </Typography>
    </div>
  );
};

const RentalCheckout = ({
  changeFormFieldValue,
  handleSubmit,
  title,
  widgetForm,
  isSummary,
  productData,
  wrapperClass,
  isAccommodationBooking,
  productsMenuIndex
}) => {
  const classes = useStyles();
  const [t] = useTranslation();

  const currency = widgetForm ? widgetForm.values.currency : '';

  return (
    <div className={classNames(classes.wrapper, wrapperClass)}>
      <div className={classes.leftSide}>
        <Typography variant="h3" classes={{ root: classes.sectionTitleIndent }}>
          {t(title)}
        </Typography>

        <form onSubmit={handleSubmit} className={classes.list}>
          {isSummary && (
            <Typography variant="h3" className={classes.summaryTitle}>
              Summary
            </Typography>
          )}
          <GeneralCheckout
            changeFormFieldValue={changeFormFieldValue}
            widgetForm={widgetForm}
            productData={productData}
            currency={currency}
            isAccommodationBooking={isAccommodationBooking}
            productsMenuIndex={productsMenuIndex}
          />
          <Total
            widgetForm={widgetForm}
            productData={productData}
            currency={currency}
            isAccommodationBooking={isAccommodationBooking}
          />
        </form>
      </div>
      <div className={classes.rightSide}>
        <BookingInformation data={productData} form={widgetForm} />
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  return {
    widgetForm: state.form.widgetForm
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('widgetForm', field, value));
  }
});

export default reduxForm({
  form: 'widgetForm',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  touchOnBlur: false
})(connect(mapStateToProps, mapDispatchToProps)(RentalCheckout));
